import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../hooks/useAccess";
import { PRICING_PAGE_ENABLED } from "../config/featureFlags";
import AuthModal from "./AuthModal";
import PolicyUpdateModal from "./PolicyUpdateModal";
import "../App.css";

export default function Header() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { access } = useAccess();
  const isAdminOrOwner = access?.role === "admin" || access?.role === "owner";
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authContextReason, setAuthContextReason] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [policyGate, setPolicyGate] = useState(null); // { requiredPolicies, onAccepted } | null

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  // Open auth modal from any page via custom event
  useEffect(() => {
    function handleOpen(e) {
      setAuthContextReason(e.detail?.reason ?? null);
      setShowAuthModal(true);
    }
    window.addEventListener("auth:open", handleOpen);
    return () => window.removeEventListener("auth:open", handleOpen);
  }, []);

  // Generic policy-update gate — any generation hook can trigger this from
  // anywhere in the app, same event-driven pattern as auth:open above.
  useEffect(() => {
    function handleOpen(e) {
      setPolicyGate({ requiredPolicies: e.detail?.requiredPolicies ?? [], onAccepted: e.detail?.onAccepted });
    }
    window.addEventListener("policy:update-required", handleOpen);
    return () => window.removeEventListener("policy:update-required", handleOpen);
  }, []);

  // Close dropdown on sign out
  async function handleSignOut() {
    setDropdownOpen(false);
    await signOut();
  }

  function handleAccountClick() {
    setDropdownOpen(false);
    navigate("/account");
  }

  return (
    <>
      <header className="app-header">
        <div className="nav-inner">
          <button className="nav-logo" onClick={() => navigate("/")}>
            WHO_WOULD_WIN
            <span className="nav-beta-badge">BETA</span>
          </button>

          <nav className="nav-links">
            <NavLink
              to="/"
              end
              className={({ isActive }) => `nav-link${isActive ? " nav-link-active" : ""}`}
            >
              Home
            </NavLink>
            <NavLink
              to="/trending"
              className={({ isActive }) => `nav-link${isActive ? " nav-link-active" : ""}`}
            >
              Trending
            </NavLink>
            <NavLink
              to="/history"
              className={({ isActive }) => `nav-link${isActive ? " nav-link-active" : ""}`}
            >
              History
            </NavLink>
            {(PRICING_PAGE_ENABLED || isAdminOrOwner) && (
              <NavLink
                to="/pricing"
                className={({ isActive }) => `nav-link${isActive ? " nav-link-active" : ""}`}
              >
                Pricing
              </NavLink>
            )}
          </nav>

          <div className="nav-right">
            {user ? (
              <div className="nav-user" ref={dropdownRef}>
                <button
                  className="nav-username-btn"
                  onClick={() => setDropdownOpen((o) => !o)}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                >
                  {user.username ?? user.email?.split("@")[0] ?? "Account"}
                  <span className="nav-username-chevron">▾</span>
                </button>

                {dropdownOpen && (
                  <div className="nav-dropdown" role="menu">
                    <button
                      className="nav-dropdown-item"
                      role="menuitem"
                      onClick={handleAccountClick}
                    >
                      Account
                    </button>
                    <button
                      className="nav-dropdown-item"
                      role="menuitem"
                      onClick={handleSignOut}
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button className="nav-auth-btn" onClick={() => { setAuthContextReason(null); setShowAuthModal(true); }}>
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        contextReason={authContextReason}
      />

      <PolicyUpdateModal
        isOpen={Boolean(policyGate)}
        requiredPolicies={policyGate?.requiredPolicies}
        onClose={() => setPolicyGate(null)}
        onAccepted={() => {
          const onAccepted = policyGate?.onAccepted;
          setPolicyGate(null);
          onAccepted?.();
        }}
      />
    </>
  );
}
